/**
 * Sentry SDK initialization for the frontend.
 *
 * Must be imported as early as possible (before React renders) so that
 * unhandled promise rejections and global errors are captured from the start.
 *
 * Env var: VITE_SENTRY_DSN — when empty or absent, Sentry is disabled and
 * all exports become safe no-ops.
 */

import * as Sentry from '@sentry/react';

const dsn = import.meta.env.VITE_SENTRY_DSN || '';
const isEnabled = Boolean(dsn);

if (isEnabled) {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE, // "development" | "production"
    release: import.meta.env.VITE_RELEASE || undefined,

    // Capture 100 % of errors, sample 20 % of transactions.
    sampleRate: 1.0,
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.2'),

    // Scrub sensitive query-string params from breadcrumbs/URLs.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'navigation' && breadcrumb.data?.to) {
        try {
          const url = new URL(breadcrumb.data.to, window.location.origin);
          url.searchParams.delete('token');
          url.searchParams.delete('code');
          breadcrumb.data.to = url.pathname + url.search;
        } catch {
          // leave as-is
        }
      }
      return breadcrumb;
    },

    // Don't send noisy ResizeObserver errors that browsers generate
    // harmlessly when elements resize during animations.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
  });
}

/**
 * Capture an exception to Sentry.  Safe no-op when Sentry is disabled.
 *
 * @param {Error} error
 * @param {Record<string, unknown>} [extra]  Additional context attached to the event.
 */
export function captureException(error, extra) {
  if (!isEnabled) return;
  Sentry.captureException(error, extra ? { extra } : undefined);
}

/**
 * Set the currently-authenticated user so all subsequent events include
 * the user context.  Call with `null` on logout.
 *
 * @param {{ id: string, email?: string } | null} user
 */
export function setSentryUser(user) {
  if (!isEnabled) return;
  Sentry.setUser(user);
}

export { isEnabled as sentryEnabled };

// ---------------------------------------------------------------------------
// Dev / staging verification helper
//
// In non-production builds, expose window.__verifySentry() so engineers can
// confirm that exceptions reach Sentry without touching production UX.
//
//   > __verifySentry()          // sends a test error
//   > __verifySentry("custom")  // sends with a custom message
// ---------------------------------------------------------------------------

if (import.meta.env.MODE !== 'production') {
  window.__verifySentry = (msg) => {
    if (!isEnabled) {
      console.warn(
        '[Sentry] VITE_SENTRY_DSN is not set — Sentry is disabled. ' +
        'Set it in your .env to test capture.',
      );
      return;
    }
    const err = new Error(msg || '[AdFlow] Sentry verification test event');
    Sentry.captureException(err);
    console.info('[Sentry] Test event sent. Check your Sentry dashboard.');
  };
}
